import React, { KeyboardEventHandler, RefObject, useMemo, useRef } from 'react';
import {
  CommonCalendarActions,
  CommonCalendarState,
  DayData,
  StateSingleDatePicker
} from '../types';
import {
  addMonthsNumber,
  addYearsNumber,
  debounce,
  endDayOfWeek,
  generateDataAttributes,
  getAddDays,
  getAvailableDay,
  getModifiers,
  isDateOutOfView,
  isDayBefore,
  isValidModifier,
  KEY_ARROW_DOWN,
  KEY_ARROW_LEFT,
  KEY_ARROW_RIGHT,
  KEY_ARROW_UP,
  KEY_END,
  KEY_ENTER,
  KEY_ESC,
  KEY_HOME,
  KEY_PAGE_DOWN,
  KEY_PAGE_UP,
  KEY_SPACE,
  monthInRange,
  nextMonthDateInSameDayWeek,
  nextYearDateInSameDayWeek,
  previousMonthDateInSameDayWeek,
  previousYearDateInSameDayWeek,
  startDayOfWeek,
  subMonthsNumber,
  subYearsNumber
} from './helper';

export type CalendarDispatchAction = (action: CommonCalendarActions) => void;

type CalendarDispatch = {
  dispatch: CalendarDispatchAction;
};
type CalendarState<T> = {
  state: T;
  modifiers: any;
  modifiersColors: any;
  format: string;
  numberOfMonths: number;
};

type CalendarProviderProps<T> = CalendarState<T> &
  CalendarDispatch & {
    children: React.ReactNode;
  };

const CalendarStateContext = React.createContext<CalendarState<CommonCalendarState> | undefined>(
  undefined
);
const CalendarDispatchContext = React.createContext<CalendarDispatchAction | undefined>(undefined);

export function CalendarProvider({
  children,
  state,
  dispatch,
  modifiers,
  modifiersColors,
  format,
  numberOfMonths
}: CalendarProviderProps<StateSingleDatePicker>) {
  const staticValue = useMemo(() => {
    return { state, modifiersColors, modifiers, format, numberOfMonths };
  }, [state, modifiersColors, modifiers, format, numberOfMonths]);
  return (
    <CalendarStateContext.Provider value={staticValue}>
      <CalendarDispatchContext.Provider value={dispatch}>
        {children}
      </CalendarDispatchContext.Provider>
    </CalendarStateContext.Provider>
  );
}

/**
 * Retrieve the dispatch method
 */
export function useCalendarDispatch() {
  const context = React.useContext(CalendarDispatchContext);
  if (context === undefined) {
    throw new Error('useCalendar Hook must be used within a CalendarProvider');
  }
  return context;
}

/**
 * Retrieve the state of Calendar
 */
export function useCalendarState() {
  const context = React.useContext(CalendarStateContext);

  if (context === undefined) {
    throw new Error('useCalendarState must be used within a CalendarProvider');
  }
  return context;
}

/**
 * Return classes, styles and modifiers of date
 * @param date
 */
export function useModifiers(
  date?: Date | null
): { style: any; modifier: any; attributes: Record<string, string> } {
  const { modifiers, modifiersColors } = useCalendarState();

  if (!date) {
    return { style: {}, modifier: {}, attributes: {} };
  }

  const dateModifiers = getModifiers(date, modifiers);
  const attributes = generateDataAttributes(dateModifiers);

  return { attributes, modifier: dateModifiers, style: modifiersColors };
}

type DebounceFunction = (fn: Function, timeout?: number) => Function;

export function useCalendarActions(focusOnlyUserSelected: boolean) {
  const { state, modifiers, numberOfMonths } = useCalendarState();
  const dispatch = useCalendarDispatch();

  const firstDate = new Date(state.today.getFullYear(), state.today.getMonth(), 1);
  const lastDate = getAddDays(state.today, state.availableDays - 1);

  const previousMonthDate = subMonthsNumber(state.displayDate, 1);

  // const isValidDate = (date: Date) => {
  //   const dateModifier = getModifiers(date, modifiers);
  //   return !dateModifier.disabled && dateModifier.interactive && !dateModifier.blackout;
  // };

  const isEnabledNextMonth = isDateOutOfView(lastDate, numberOfMonths, state.displayDate);
  const isEnabledPreviousMonth = monthInRange(previousMonthDate, {
    start: firstDate,
    end: lastDate
  });

  const setPreviousMonth = () => {
    // const date = previousMonthDateInSameDayWeek(state.displayDate, isValidDate);
    // if (isEnabledPreviousMonth) {
    //   dispatch({ type: 'SET_DISPLAY_DATE_ACTION', payload: date });
    //   if (!focusOnlyUserSelected) {
    //     if (state.currentDate?.start) {
    //       if (!isDateOutOfView(state.currentDate?.start, numberOfMonths, date)) {
    //         dispatch({
    //           type: 'SET_PRESELECTED_DATE',
    //           payload: state.currentDate?.start
    //         });
    //       } else {
    //         dispatch({
    //           type: 'SET_PRESELECTED_DATE',
    //           payload: date
    //         });
    //       }
    //     } else {
    //       dispatch({
    //         type: 'SET_PRESELECTED_DATE',
    //         payload: isBefore(date, state.today) ? state.today : date
    //       });
    //     }
    //   }
    // }
  };
  const setNextMonth = () => {
    // const date = nextMonthDateInSameDayWeek(state.displayDate, isValidDate);
    // if (isEnabledNextMonth) {
    //   dispatch({ type: 'SET_DISPLAY_DATE_ACTION', payload: date });
    //   if (!focusOnlyUserSelected) {
    //     //evita que primero se haga el SET_PRESELECTED_DATE
    //     setTimeout(() => {
    //       if (state.currentDate?.start) {
    //         if (!isDateOutOfView(state.currentDate?.start, numberOfMonths, date)) {
    //           dispatch({
    //             type: 'SET_PRESELECTED_DATE',
    //             payload: state.currentDate?.start
    //           });
    //         } else {
    //           dispatch({
    //             type: 'SET_PRESELECTED_DATE',
    //             payload: date
    //           });
    //         }
    //       } else {
    //         dispatch({
    //           type: 'SET_PRESELECTED_DATE',
    //           payload: date
    //         });
    //       }
    //     }, 5);
    //   }
    // }
  };

  const setPreviousYear = () => {
    const previousYearDate = subYearsNumber(state.displayDate, 1);
    const newModifiers = getModifiers(previousYearDate, modifiers);

    if (isValidModifier(newModifiers)) {
      dispatch({ type: 'SET_DISPLAY_DATE_ACTION', payload: previousYearDate });
    }
  };
  const setNextYear = () => {
    const nextYearDate = addYearsNumber(state.displayDate, 1);
    const newModifiers = getModifiers(nextYearDate, modifiers);

    if (isValidModifier(newModifiers)) {
      dispatch({ type: 'SET_DISPLAY_DATE_ACTION', payload: nextYearDate });
    }
  };

  return {
    setPreviousMonth,
    setNextMonth,
    setNextYear,
    setPreviousYear,
    isEnabledPreviousMonth,
    isEnabledNextMonth
  };
}

/**
 * Debounce Function
 * @param fn
 * @param timeout
 * @usage:
 * const debounceOnChange = useDebounce((value: string) => {
 * dispatch({ type: 'PARSE_INPUT_ACTION', payload: value });
 *  });
 */
export const useDebounce: DebounceFunction = (fn, timeout = 500) => {
  return useRef<typeof fn>(debounce(fn, timeout)).current;
};

export interface FnOnDayClick {
  (data: DayData, component?: 'start' | 'end'): void;
}
export interface FnOnDayHover {
  (event: $TSFixMe, data: DayData): void;
}

export interface NavigationValidationFunctions {
  validateDay: (date: Date) => boolean;
  dataFromDay: (date: Date) => DayData | null;
  isDateOutOfView: (date: Date) => boolean;
}

interface UseHandlerCalendarArguments<T = any> {
  initialDate: Date | undefined;
  preSelected: Date | undefined;
  format: string;
  availableDays?: number;
  validations: NavigationValidationFunctions;
  isPopopverOpen: boolean;
  dispatch: Function;
  inputRef?: RefObject<T>;
  component?: 'start' | 'end';
  onDayClick?: FnOnDayClick;
  setWasOpenPopup?: (value: boolean) => void;
  selectedRange?: Set<number>;
}
/**
 * Handle the keyboard movement through datepicker
 * @param inputRef
 * @param format DateFormat
 * @param dispatch
 * @param modifiers
 * @param component
 * @param onDayClick
 */
export function useHandleCalendarNavigation<T>({
  initialDate,
  format,
  validations,
  isPopopverOpen,
  dispatch,
  inputRef,
  component,
  onDayClick,
  preSelected,
  setWasOpenPopup,
  availableDays,
  selectedRange
}: UseHandlerCalendarArguments) {
  const setMonthToShow = (key: any, newDate: Date) => {
    if (key === KEY_PAGE_DOWN && availableDays) {
      const nextMonth = addMonthsNumber(newDate, 1);
      const firstDateNextMonth = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 1);
      const lastDate = getAddDays(new Date(), availableDays);

      if (isDayBefore(firstDateNextMonth, lastDate)) {
        dispatch({ type: 'SET_DISPLAY_DATE_ACTION', payload: newDate });
      } else {
        dispatch({
          type: 'SET_DISPLAY_DATE_ACTION',
          payload: subMonthsNumber(newDate, 1)
        });
      }
    } else if (key === KEY_PAGE_UP) {
      dispatch({
        type: 'SET_DISPLAY_DATE_ACTION',
        payload: newDate
      });
    }
    dispatch({ type: 'CHANGE_CURRENT_MONTH', payload: -(Math.random() + 0.5) });
  };
  const selectDate = () => {
    if (selectedRange === null) {
      // dispatch({
      //   type: 'SET_DATE_ACTION',
      //   payload: preSelected,
      //   component: component,
      //   close: true
      // });
    } else {
      // dispatch({
      //   type: 'SET_DATE_ACTION',
      //   payload: preSelected,
      //   component: component,
      //   close: true
      // });
    }
    setWasOpenPopup?.(true);
  };

  React.useEffect(() => {
    inputRef?.current?.addEventListener('onKeyPress', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputRef]);

  const handleKeyDown: KeyboardEventHandler<T> = (event: any) => {
    typeof event.persist === 'function' && event.persist();
    event.stopPropagation();
    const whiteList = [
      KEY_ARROW_DOWN,
      KEY_ARROW_UP,
      KEY_ARROW_LEFT,
      KEY_ARROW_RIGHT,
      KEY_SPACE,
      KEY_ESC,
      KEY_ENTER,
      KEY_PAGE_DOWN,
      KEY_PAGE_UP
    ];

    let newDate = new Date();
    const preselectedDate = initialDate ?? new Date();

    const enter = () => {
      event.preventDefault();
      if (inputRef && event.currentTarget === inputRef.current && component) {
        if (component === 'start') {
        } else if (component === 'end') {
        } else {
          dispatch({ type: 'CLEAR_ACTION' });
        }
      } else {
        const dayData = validations.dataFromDay(preSelected ?? newDate);
        if (onDayClick && dayData) {
          onDayClick(dayData, component);
          selectDate();
        }
      }
    };

    //Ignore others keys if popover not its open
    if (!isPopopverOpen && event.which !== KEY_ENTER) {
      if (event.key === 'Enter' && event.code === 'Enter') {
        enter();
      }
      return;
    }

    switch (event.which) {
      case KEY_ARROW_RIGHT:
        if (inputRef && event.currentTarget === inputRef.current) {
          return;
        }
        newDate = getAvailableDay(preselectedDate, 1, validations.validateDay, 'next');
        break;
      case KEY_ARROW_LEFT:
        if (inputRef && event.currentTarget === inputRef.current) {
          return;
        }
        newDate = getAvailableDay(preselectedDate, 1, validations.validateDay, 'previous');
        break;
      case KEY_ARROW_UP:
        if (inputRef && event.currentTarget === inputRef.current) {
          return;
        }
        event.preventDefault();
        newDate = getAvailableDay(preselectedDate, 7, validations.validateDay, 'previous');
        break;
      case KEY_ARROW_DOWN:
        if (inputRef && event.currentTarget === inputRef.current) {
          return;
        }
        event.preventDefault();
        newDate = getAvailableDay(preselectedDate, 7, validations.validateDay, 'next');
        break;
      case KEY_ESC:
        dispatch({ type: 'CANCEL_ACTION' });
        break;
      case KEY_HOME:
        newDate = startDayOfWeek(preselectedDate);
        break;
      case KEY_END:
        newDate = endDayOfWeek(preselectedDate);
        break;
      case KEY_PAGE_UP:
        event.preventDefault();
        if (event.shiftKey) {
          newDate = previousYearDateInSameDayWeek(preselectedDate, validations.validateDay);
        } else {
          newDate = previousMonthDateInSameDayWeek(preselectedDate, validations.validateDay);
        }
        dispatch({ type: 'CHANGE_CURRENT_MONTH', payload: -(Math.random() + 0.5) });
        break;
      case KEY_PAGE_DOWN:
        event.preventDefault();
        if (event.shiftKey) {
          newDate = nextYearDateInSameDayWeek(preselectedDate, validations.validateDay);
        } else {
          newDate = nextMonthDateInSameDayWeek(preselectedDate, validations.validateDay);
        }
        if (availableDays) {
          dispatch({ type: 'CHANGE_CURRENT_MONTH', payload: -(Math.random() + 0.5) });
        }
        break;
      case KEY_SPACE: {
        event.preventDefault();
        const dayData = validations.dataFromDay(preselectedDate);
        if (
          onDayClick &&
          dayData &&
          (!inputRef || (inputRef && inputRef.current !== event.currentTarget))
        ) {
          onDayClick(dayData, component);
          selectDate();
        } else {
          return;
        }
      }
      // eslint-disable-next-line no-fallthrough
      case KEY_ENTER:
        enter();
        return;
      default:
        break;
    }

    if (event.key === 'Enter' && event.code === 'Enter') {
      enter();
      return;
    }
    if (validations.validateDay(newDate) && whiteList.includes(event.which)) {
      // if(component ==='start') {
      dispatch({ type: 'SET_PRESELECTED_DATE', payload: newDate });
      if (validations.isDateOutOfView(newDate)) {
        if (event.which === KEY_PAGE_DOWN || event.which === KEY_PAGE_UP) {
          setMonthToShow(event.which, newDate);
          dispatch({ type: 'SET_DISPLAY_DATE_ACTION', payload: newDate });
          return;
        }
        dispatch({ type: 'SET_DISPLAY_DATE_ACTION', payload: newDate });
      }
    }
  };

  return handleKeyDown;
}
export const useEffectOnlyOnce = (callback: Function, dependencies: Array<any>) => {
  const calledOnce = React.useRef(false);

  React.useEffect(() => {
    if (calledOnce.current) {
      return;
    }

    callback(dependencies);
    calledOnce.current = true;
  }, [callback, dependencies]);
};
